/**
 * Mode select + top-level menu router — title screen, mode list, MP lobby.
 */

import { Keys } from 'bonkjs';
import type { InputProvider } from '../../input';
import type { GameState } from '../../types';
import type { GameEffect } from '../effects';
import { transitionPhase } from '../effects';
import { createModeState } from '../mode';
import { resetGame } from '../reset';
import { TURN_FLOW, ASCENT } from '../../constants';
import { setWorldHeight, setWorldWidth } from '../../constants';
import { generateQuickplayName } from '../../net/names';
import { justPressed, msToFrames } from '../input-edge';
import { getAllTankIds } from '../../tanks';
import { DEFAULT_STAGE_ID, setCustomThemeOverride } from '../../stages';
import { tankColor } from './helpers';
import { handleTankSelectInput } from './tank-select';
import { handleStageSelectInput } from './stage-select';
import { handleSkinsShopInput } from './skins-shop-input';
import { playSound } from '../../audio';

// ─── Mode Config ─────────────────────────────────────────────

// Mirrors ModeSelectRenderer's MENU_ITEMS array
// Items: 0=ONLINE 1V1 (hero), 1=LOCAL, 2=SURVIVAL, 3=CLASSIC, 4=header "HOW TO PLAY",
//        5=OFFENSE, 6=DEFENSE, 7=header "EXTRAS", 8=WEAPON LAB, 9=TARGET BLAST, 10=SKINS SHOP
type ModeItem = { type: 'header' } | { type: 'mode'; id: string; enabled: boolean };
const MODE_ITEMS: ModeItem[] = [
  { type: 'mode', id: 'online', enabled: true },
  { type: 'mode', id: 'local', enabled: true },
  { type: 'mode', id: 'survival', enabled: true },
  { type: 'mode', id: 'classic', enabled: true },
  { type: 'header' },
  { type: 'mode', id: 'tutorial_offense', enabled: true },
  { type: 'mode', id: 'tutorial_defense', enabled: true },
  { type: 'header' },
  { type: 'mode', id: 'training', enabled: true },
  { type: 'mode', id: 'target_blast', enabled: true },
  { type: 'mode', id: 'skins_shop', enabled: true },
];

function getModeCount(): number { return MODE_ITEMS.length; }
function isModeEnabled(index: number): boolean {
  const item = MODE_ITEMS[index];
  return item?.type === 'mode' && item.enabled;
}
function getModeId(index: number): string {
  const item = MODE_ITEMS[index];
  return item?.type === 'mode' ? item.id : '';
}

// ─── Menu Input ──────────────────────────────────────────────

export function handleMenuInput(state: GameState, input: InputProvider, fx: GameEffect[]): void {
  if (state.phase === 'title_screen') {
    if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
      transitionPhase(state, 'mode_select', fx);
      state.menuSelectIndex = 0; // ONLINE 1V1 (hero) at top
    }
    return;
  }

  if (state.phase === 'mode_select') {
    // Navigate — skip section headers (non-selectable entries at getModeItemType !== 'mode')
    if (justPressed(Keys.ArrowUp, input) || justPressed(Keys.W, input)) {
      let idx = state.menuSelectIndex - 1;
      while (idx >= 0 && !isModeEnabled(idx)) idx--;
      if (idx >= 0) { state.menuSelectIndex = idx; playSound('menu_nav'); }
    }
    if (justPressed(Keys.ArrowDown, input) || justPressed(Keys.S, input)) {
      let idx = state.menuSelectIndex + 1;
      while (idx < getModeCount() && !isModeEnabled(idx)) idx++;
      if (idx < getModeCount()) { state.menuSelectIndex = idx; playSound('menu_nav'); }
    }

    // Confirm
    if ((justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) && isModeEnabled(state.menuSelectIndex)) {
      playSound('menu_select');
      setCustomThemeOverride(null);
      const modeId = getModeId(state.menuSelectIndex);

      if (modeId === 'online') {
        // ONLINE — enter multiplayer lobby
        setWorldHeight(1440); setWorldWidth(2560);
        state.modeState = createModeState('vs_online');
        transitionPhase(state, 'mp_lobby', fx);
        state.multiplayer = {
          enabled: false,
          mySlot: 0,
          opponentName: '',
          roomCode: '',
          connected: false,
          wins: [0, 0],
          lobbyAction: 'choose',
          lobbySelectIndex: 0,
          roomCodeInput: '',
          lobbyStatus: '',
          quickplayName: '',
          opponentReady: false,
          stageSelectChooser: 0,
          disconnectPhase: null,
          disconnectCountdownEnd: 0,
          disconnectCursor: 0,
        };
      } else if (modeId === 'local') {
        // LOCAL 1v1 — enter tank select before starting game
        setWorldHeight(1440); setWorldWidth(2560);
        state.modeState = createModeState('vs_local');
        state.tankSelectReady = [false, false];
        state.tankSelectCursor = [0, 0];
        for (const p of state.players) {
          p.tankId = getAllTankIds()[0];
          p.color = tankColor(p.tankId);
        }
        // Default P2 to CPU when only one input device, human when two gamepads
        const p2 = state.players[1];
        if (state.twoGamepadsConnected) {
          p2.isAI = false;
          p2.aiDifficulty = 0;
          p2.name = 'P2';
        } else {
          p2.isAI = true;
          if (p2.aiDifficulty === 0) p2.aiDifficulty = 5;
          p2.name = 'CPU';
        }
        transitionPhase(state, 'tank_select', fx);
      } else if (modeId === 'training') {
        // TRAINING — solo freefire sandbox
        setWorldHeight(1440); setWorldWidth(2560);
        state.modeState = createModeState('training');
        state.tankSelectReady = [false, true]; // P2 auto-ready (solo mode)
        state.tankSelectCursor = [0, 0];
        state.players[0].tankId = getAllTankIds()[0];
        state.players[0].color = tankColor(state.players[0].tankId);
        transitionPhase(state, 'tank_select', fx);
      } else if (modeId === 'target_blast') {
        // TARGET BLAST — solo target shooting
        setWorldHeight(1440); setWorldWidth(2560);
        state.modeState = createModeState('target_blast');
        state.roundNumber = 0;
        fx.push(...resetGame(state));
        state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
      } else if (modeId === 'cosmic_lottery') {
        // COSMIC LOTTERY — random weapons each turn
        setWorldHeight(1440); setWorldWidth(2560);
        state.modeState = createModeState('cosmic_lottery');
        state.roundNumber = 0;
        fx.push(...resetGame(state));
        state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
      } else if (modeId === 'survival') {
        // SURVIVAL — 1P endless enemy waves, pick your tank
        setWorldHeight(1440); setWorldWidth(2560);
        state.modeState = createModeState('survival');
        state.tankSelectReady = [false, true]; // P2 auto-ready (solo mode)
        state.tankSelectCursor = [0, 0];
        state.players[0].tankId = getAllTankIds()[0];
        state.players[0].color = tankColor(state.players[0].tankId);
        transitionPhase(state, 'tank_select', fx);
      } else if (modeId === 'classic') {
        // CLASSIC ARTILLERY — HP damage, tank select, 3 shot tiers
        setWorldHeight(1440); setWorldWidth(2560);
        state.modeState = createModeState('classic');
        state.tankSelectReady = [false, false];
        state.tankSelectCursor = [0, 0];
        for (const p of state.players) {
          p.tankId = getAllTankIds()[0];
          p.color = tankColor(p.tankId);
        }
        // Default P2 to CPU when only one input device, human when two gamepads
        const cp2 = state.players[1];
        if (state.twoGamepadsConnected) {
          cp2.isAI = false;
          cp2.aiDifficulty = 0;
          cp2.name = 'P2';
        } else {
          cp2.isAI = true;
          if (cp2.aiDifficulty === 0) cp2.aiDifficulty = 5;
          cp2.name = 'CPU';
        }
        transitionPhase(state, 'tank_select', fx);
      } else if (modeId === 'tutorial_offense' || modeId === 'tutorial_defense') {
        // TUTORIAL — forced mortar, skip tank/stage select, go straight to game
        setWorldHeight(1440); setWorldWidth(2560);
        state.modeState = createModeState('tutorial');
        state.modeState.tutorialVariant = modeId === 'tutorial_defense' ? 'defense' : 'offense';
        // Force mortar for both players
        for (const p of state.players) {
          p.tankId = 'mortar';
          p.color = tankColor('mortar');
        }
        // P2 = AI dummy (stands still)
        state.players[1].isAI = true;
        state.players[1].aiDifficulty = 0; // passive dummy
        // Use Final Destination (simple flat stage for training)
        state.stageId = 'final_destination';
        fx.push({ type: 'stage_theme_change', stageId: 'final_destination' });
        state.roundNumber = 0;
        fx.push(...resetGame(state));
        state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
      } else if (modeId === 'skins_shop') {
        // SKINS SHOP — per-tank visual customization
        state.skinsShopTank = 0;
        state.skinsShopSection = 0;
        state.skinsShopItem = 0;
        transitionPhase(state, 'skins_shop', fx);
      }
    }

    // Back
    if (justPressed(Keys.Escape, input)) {
      transitionPhase(state, 'title_screen', fx);
      fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
    }
    return;
  }

  // ─── Skins Shop ─────────────────────────────────────
  if (state.phase === 'skins_shop') {
    handleSkinsShopInput(state, input, fx);
    return;
  }

  // ─── Multiplayer Lobby ─────────────────────────────────────
  if (state.phase === 'mp_lobby' && state.multiplayer) {
    const mp = state.multiplayer;

    if (mp.lobbyAction === 'choose') {
      // Choose between Create Room / Join Room / Quickplay
      if (justPressed(Keys.ArrowUp, input) || justPressed(Keys.W, input)) {
        const prev = mp.lobbySelectIndex;
        mp.lobbySelectIndex = Math.max(0, mp.lobbySelectIndex - 1);
        if (mp.lobbySelectIndex !== prev) playSound('menu_nav');
      }
      if (justPressed(Keys.ArrowDown, input) || justPressed(Keys.S, input)) {
        const prev = mp.lobbySelectIndex;
        mp.lobbySelectIndex = Math.min(2, mp.lobbySelectIndex + 1);
        if (mp.lobbySelectIndex !== prev) playSound('menu_nav');
      }
      if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
        playSound('menu_select');
        if (mp.lobbySelectIndex === 0) {
          mp.lobbyAction = 'quickplay';
          mp.quickplayName = generateQuickplayName();
          mp.lobbyStatus = 'Searching...';
        } else if (mp.lobbySelectIndex === 1) {
          mp.lobbyAction = 'create';
          mp.lobbyStatus = 'Connecting...';
        } else {
          mp.lobbyAction = 'join';
          mp.roomCodeInput = '';
        }
      }
      if (justPressed(Keys.Escape, input)) {
        state.multiplayer = null;
        transitionPhase(state, 'mode_select', fx);
      }
    } else if (mp.lobbyAction === 'quickplay') {
      // Searching for opponent — ESC to cancel
      if (justPressed(Keys.Escape, input)) {
        mp.lobbyAction = 'choose';
        mp.lobbyStatus = '';
        mp.quickplayName = '';
      }
    } else if (mp.lobbyAction === 'join') {
      // Room code text entry is handled by the multiplayer manager
      // (keyboard events are captured separately for typing)
      // Space to confirm when code is 4+ chars
      if ((justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) && mp.roomCodeInput.length >= 4) {
        mp.lobbyStatus = 'Joining...';
      }
      if (justPressed(Keys.Escape, input)) {
        mp.lobbyAction = 'choose';
        mp.roomCodeInput = '';
      }
    } else if (mp.lobbyAction === 'create') {
      // Waiting for room code from server
      if (justPressed(Keys.Escape, input)) {
        mp.lobbyAction = 'choose';
        mp.lobbyStatus = '';
      }
    }
    return;
  }

  if (state.phase === 'tank_select') {
    handleTankSelectInput(state, input, fx);
    return;
  }

  if (state.phase === 'stage_select') {
    handleStageSelectInput(state, input, fx);
    return;
  }

  if (state.phase === 'mp_waiting') {
    // Waiting for opponent — ESC to cancel
    if (justPressed(Keys.Escape, input)) {
      state.multiplayer = null;
      transitionPhase(state, 'mode_select', fx);
    }
    return;
  }
}
